import React from 'react';

import { FlexBox } from 'ml-rubiks-styleguide/src/box';

import { useI18n } from '../../contexts/i18n-context';
import { usePaymentErrorContext } from '../../contexts/payment-error-context';
import { PaymentProcessor } from '../../core/payment-processor';
import { PaymentMethod, SavedPaymentMethod } from '../../types';
import { AvailablePaymentMethodsV1Dto } from '../../types/api';
import {
    getPaymentMethodTypeName,
    isDirectPaymentMethod
} from '../../utils/payment-methods';
import { LoadingMessage, PaymentLayout, UnifiedError } from '../common';
import { PaymentMethodTypeSelector } from '../payment-method-types/payment-method-type-selector';
import { PaymentWidgetContainer } from '../payment-widget/payment-widget-container';
import { SavedPaymentMethodList } from '../saved-payment-methods/saved-payment-method-list';

type ViewState = 'loading' | 'main' | 'selecting-type' | 'adding-method';

interface PaymentMethodViewProps {
    // State
    currentView: ViewState;
    selectedMethod: string | null;
    currentMethodType: PaymentMethod | null;
    processingMethodId: string | null;
    isPaymentLoading: boolean;
    isLoadingAddNew: boolean;
    loadingMethodType: PaymentMethod | null;

    // Data
    savedMethods: SavedPaymentMethod[];
    isLoading: boolean;
    paymentProcessor: PaymentProcessor;
    availablePaymentMethods: AvailablePaymentMethodsV1Dto[];
    isLoadingAvailableMethods: boolean;
    hasLoadedAvailableMethods: boolean;
    hasAvailableMethodsError: boolean;

    // Event handlers
    handlers: {
        handleMethodSelect: (methodId: string) => Promise<void>;
        handleStartAddNew: () => void;
        handleTypeSelect: (methodType: PaymentMethod) => void;
        handleCancelAddNew: () => void;
        handleCancelToTypeSelection: () => void;
        handleCancelToMain: () => void;
        handlePaymentMethodAdded: () => void;
    };

    // Route context - determines if back button should show
    isDirectRoute?: boolean;
}

export const PaymentMethodView: React.FC<PaymentMethodViewProps> = React.memo(
    ({
        currentView,
        selectedMethod,
        currentMethodType,
        processingMethodId,
        isPaymentLoading,
        isLoadingAddNew,
        loadingMethodType,
        savedMethods,
        isLoading,
        paymentProcessor,
        availablePaymentMethods,
        isLoadingAvailableMethods,
        hasLoadedAvailableMethods,
        hasAvailableMethodsError,
        handlers,
        isDirectRoute = false
    }) => {
        const { t, isHidden } = useI18n();
        const { state: errorState } = usePaymentErrorContext();

        const availableMethods = availablePaymentMethods.flatMap(
            (provider) =>
                provider.paymentMethods?.map(
                    (method) => method.paymentMethod
                ) || []
        );
        const uniqueAvailableMethods = Array.from(new Set(availableMethods));
        const nonDirectMethods = uniqueAvailableMethods.filter(
            (method) => !isDirectPaymentMethod(method)
        );

        const hasValidMethods =
            hasLoadedAvailableMethods && !hasAvailableMethodsError;
        const hasSingleMethod = uniqueAvailableMethods.length === 1;
        const hasMultipleMethods = uniqueAvailableMethods.length > 1;
        const hasSingleNonDirectMethod =
            hasSingleMethod && nonDirectMethods.length === 1;
        const hasNoSavedMethods = savedMethods.length === 0;

        const willAutoNavigate =
            hasValidMethods &&
            ((currentView === 'main' &&
                hasNoSavedMethods &&
                (hasSingleNonDirectMethod || hasMultipleMethods)) ||
                (currentView === 'selecting-type' && hasSingleMethod));

        const hasErrors = errorState.errors.length > 0;

        const isStillLoading =
            isLoading ||
            (!hasLoadedAvailableMethods && !hasAvailableMethodsError);
        const isUnifiedLoading =
            !hasErrors && (isStillLoading || willAutoNavigate);

        const hasNoPaymentMethods =
            !isUnifiedLoading &&
            hasNoSavedMethods &&
            hasValidMethods &&
            uniqueAvailableMethods.length === 0;

        const hasInitialLoadError =
            hasErrors && !hasValidMethods && hasNoSavedMethods;

        const canAddNewMethods =
            hasValidMethods && uniqueAvailableMethods.length > 0;

        if (isUnifiedLoading) {
            return (
                <PaymentLayout>
                    <FlexBox hAlign="center" padding={[4, 4]}>
                        <LoadingMessage
                            message={t(
                                'upc.loading.payment.methods',
                                'Loading payment methods...'
                            )}
                        />
                    </FlexBox>
                </PaymentLayout>
            );
        }

        if (hasInitialLoadError) {
            return <PaymentLayout />;
        }

        if (hasNoPaymentMethods) {
            if (isHidden('upc.warning.no.payment.methods')) {
                return null;
            }

            return (
                <PaymentLayout>
                    <UnifiedError
                        severity="warning"
                        message={t(
                            'upc.warning.no.payment.methods',
                            'No payment methods available. Please contact support.'
                        )}
                    />
                </PaymentLayout>
            );
        }

        if (currentView === 'main') {
            return (
                <PaymentLayout
                    title={t(
                        'upc.my.payment.instruments',
                        'My payment instruments'
                    )}
                >
                    <SavedPaymentMethodList
                        methods={savedMethods}
                        selectedMethod={selectedMethod}
                        onMethodSelect={handlers.handleMethodSelect}
                        isPaymentLoading={isPaymentLoading}
                        isLoadingAddNew={isLoadingAddNew}
                        processingMethodId={processingMethodId}
                        showAddButton={canAddNewMethods}
                    />
                </PaymentLayout>
            );
        }

        if (currentView === 'selecting-type' && hasMultipleMethods) {
            return (
                <PaymentLayout
                    title={t(
                        'upc.payment.methods.select.type.title',
                        'Select Payment Method Type'
                    )}
                    onBack={
                        !isDirectRoute ? handlers.handleCancelAddNew : undefined
                    }
                >
                    <PaymentMethodTypeSelector
                        onTypeSelect={handlers.handleTypeSelect}
                        onBack={
                            !isDirectRoute
                                ? handlers.handleCancelAddNew
                                : undefined
                        }
                        paymentProcessor={paymentProcessor}
                        availablePaymentMethods={availablePaymentMethods}
                        isLoadingAvailableMethods={isLoadingAvailableMethods}
                        hasLoadedAvailableMethods={hasLoadedAvailableMethods}
                        hasAvailableMethodsError={hasAvailableMethodsError}
                        loadingMethodType={loadingMethodType}
                    />
                </PaymentLayout>
            );
        }

        if (currentView === 'adding-method' && currentMethodType) {
            const methodTypeName = getPaymentMethodTypeName(
                currentMethodType,
                t
            );

            let backHandler: (() => void) | undefined;

            if (uniqueAvailableMethods.length > 1) {
                backHandler = handlers.handleCancelToTypeSelection;
            } else if (savedMethods.length > 0) {
                backHandler = handlers.handleCancelToMain;
            }

            return (
                <PaymentLayout
                    title={t('upc.payment.methods.add.title', 'Add {0}', [
                        methodTypeName
                    ])}
                    onBack={backHandler}
                >
                    <PaymentWidgetContainer
                        methodType={currentMethodType}
                        paymentProcessor={paymentProcessor}
                        availablePaymentMethods={availablePaymentMethods}
                        onBack={backHandler}
                        onPaymentSuccess={handlers.handlePaymentMethodAdded}
                    />
                </PaymentLayout>
            );
        }

        return null;
    }
);

PaymentMethodView.displayName = 'PaymentMethodView';
